import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useDropzone } from 'react-dropzone'
import {
  AlertCircle,
  ChevronDown,
  File as FileIcon,
  FileImage,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  Loader2,
  MessageCircle,
  Search,
  Trash2,
  Upload,
  UploadCloud,
} from 'lucide-react'
import { RootState } from 'App'
import { UserFileTag } from '__generated__/api-types-and-hooks'
import { useOpenUserFile } from 'components/Common/AppFilesCard/useOpenUserFile'
import {
  countBySource,
  FileCategory,
  FileFilter,
  formatFileDate,
  formatFileSize,
  getFileCategory,
  groupFiles,
  ILibraryFile,
  isChatFile,
  isKeyFile,
  matchesQuery,
  splitFileName,
} from 'components/Common/AppFileLibrary/AppFileLibrary.utils'

// Rows a section shows at first, and how many more each "Show more" adds
const ROWS_SHOWN = 3
const ROWS_MORE = 10
// The search box appears once there are more files than this
const SEARCH_AFTER = 8
// How long a file that was just uploaded stays highlighted
const HIGHLIGHT_MS = 2000
// With this many files, only the first sections start open, so the panel stays short
const MANY_FILES = 30
const OPEN_SECTIONS = 3

const CATEGORY_STYLE: Record<FileCategory, { Icon: typeof FileIcon; tone: string; label: string }> =
  {
    documents: {
      Icon: FileText,
      tone: 'bg-app-primary-10 text-app-primary-60',
      label: 'files.sectionDocuments',
    },
    spreadsheets: {
      Icon: FileSpreadsheet,
      tone: 'bg-app-green-10 text-app-green-70',
      label: 'files.sectionSpreadsheets',
    },
    images: {
      Icon: FileImage,
      tone: 'bg-app-orange-10 text-app-orange-70',
      label: 'files.sectionImages',
    },
    other: { Icon: FileIcon, tone: 'bg-app-grey-20 text-app-grey-70', label: 'files.sectionOther' },
  }

const FILTERS: { id: FileFilter; label: string }[] = [
  { id: 'all', label: 'files.filterAll' },
  { id: 'chat', label: 'files.filterChat' },
  { id: 'uploaded', label: 'files.filterUploaded' },
]

const focusRing = 'focus:outline-none focus-visible:appShadow-focus'
// index.css gives every element (svg shapes included) its own text color, so icons and nested
// text must ask to follow their parent; the panel makes every icon's shapes follow the icon
const inheritColor = '[color:inherit]'

const sectionOf = (file: ILibraryFile): string => (isKeyFile(file) ? 'key' : getFileCategory(file))

export interface IAppFileLibraryProps {
  files: ILibraryFile[]
  /** The list is (re)loading: show the last list, or placeholders the first time */
  isLoading?: boolean
  /** The list could not be loaded: say so, with a button that calls onRetry */
  isError?: boolean
  onRetry?: () => void
  /** Lets people add files with the Upload button or by dropping them on the panel */
  onUpload?: (files: File[]) => void
  isUploading?: boolean
  /** Shows a delete button on each file. The caller asks for confirmation. */
  onDelete?: (file: ILibraryFile) => void
  deletingId?: string | null
  /** Files that were just uploaded: brought into view and highlighted for a moment */
  recentIds?: string[]
  /** "plain" sits in a page column next to other columns, without a card around it */
  variant?: 'card' | 'plain'
  /** What the empty panel says (the default speaks to the owner of the files) */
  emptyBody?: string
}

/** The Files panel: key documents first, then files grouped by kind, with search and filters. */
export const AppFileLibrary = ({
  files,
  isLoading = false,
  isError = false,
  onRetry,
  onUpload,
  isUploading = false,
  onDelete,
  deletingId,
  recentIds,
  variant = 'card',
  emptyBody,
}: IAppFileLibraryProps) => {
  const { t, i18n } = useTranslation()
  const viewerId = useSelector((state: RootState) => state.user.user?.id)
  const openFile = useOpenUserFile()
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<FileFilter>('all')
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const [limits, setLimits] = useState<Record<string, number>>({})
  const [highlighted, setHighlighted] = useState<string[]>([])
  const [dropTop, setDropTop] = useState<number | null>(null)
  const sectionHeads = useRef<Record<string, HTMLButtonElement | null>>({})
  const listRef = useRef<HTMLDivElement>(null)

  // The list is emptied while it reloads: keep showing the last one meanwhile. A panel that
  // opens while loading has shown nothing yet, so it starts with placeholders.
  const lastFiles = useRef(isLoading ? [] : files)
  if (!isLoading) lastFiles.current = files
  const current = isLoading ? lastFiles.current : files
  const shown = useMemo(() => (Array.isArray(current) ? current : []), [current])

  const showSearch = shown.length > SEARCH_AFTER
  const activeQuery = showSearch ? query.trim() : ''
  const allCounts = useMemo(() => countBySource(shown), [shown])
  const showFilter = allCounts.chat > 0 && allCounts.uploaded > 0
  const activeFilter = showFilter ? filter : 'all'
  // The filter counts follow the search, so they match what each choice would show
  const counts = useMemo(
    () => countBySource(shown.filter((file) => matchesQuery(file, activeQuery))),
    [shown, activeQuery]
  )
  const groups = useMemo(
    () => groupFiles(shown, activeFilter, activeQuery),
    [shown, activeFilter, activeQuery]
  )

  // Just uploaded: clear the search and filter, open their sections, highlight them
  useEffect(() => {
    if (!recentIds?.length) return
    const recent = new Set(recentIds)
    const perSection: Record<string, number> = {}
    shown.forEach((file) => {
      if (file.id && recent.has(file.id)) {
        const id = sectionOf(file)
        perSection[id] = (perSection[id] || 0) + 1
      }
    })
    setFilter('all')
    setQuery('')
    setCollapsed((prev) => {
      const next = { ...prev }
      Object.keys(perSection).forEach((id) => {
        next[id] = false
      })
      return next
    })
    setLimits((prev) => {
      const next = { ...prev }
      Object.entries(perSection).forEach(([id, n]) => {
        next[id] = Math.max(next[id] || ROWS_SHOWN, n)
      })
      return next
    })
    // Joins any upload still highlighted: uploads that run side by side land close together
    setHighlighted((prev) => [...prev, ...recentIds])
    const frame = requestAnimationFrame(() =>
      listRef.current
        ?.querySelector('[data-recent="true"]')
        ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    )
    const timer = setTimeout(() => setHighlighted([]), HIGHLIGHT_MS)
    return () => {
      cancelAnimationFrame(frame)
      clearTimeout(timer)
    }
    // Only when a new upload lands
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentIds])

  // Keeps the drop message in the middle of the part of the panel that is on screen
  const placeDropMessage = (event: React.DragEvent<HTMLElement>) => {
    const box = event.currentTarget?.getBoundingClientRect?.()
    if (!box) return
    const top = Math.max(box.top, 0)
    const bottom = Math.min(box.bottom, window.innerHeight)
    const middle = Math.round((top + bottom) / 2 - box.top)
    const y = Math.min(Math.max(middle, 80), Math.max(box.height - 80, 80))
    setDropTop((prev) => (prev !== null && Math.abs(prev - y) < 8 ? prev : y))
  }

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop: (accepted) => onUpload?.(accepted),
    onDragEnter: placeDropMessage,
    onDragOver: placeDropMessage,
    multiple: true,
    noClick: true,
    noKeyboard: true,
    useFsAccessApi: false,
    // Files dropped while others are still uploading go up too (each upload runs on its own)
    disabled: !onUpload,
  })

  /** Who shared or uploaded the file: the full sentence, and the short form the row shows */
  const sourceOf = (file: ILibraryFile) => {
    const byViewer = !!viewerId && file.uploadedBy === viewerId
    if (isChatFile(file)) {
      if (byViewer) return { chat: true, full: t('files.sharedInChatByYou'), short: t('chat.you') }
      return file.uploadedByName
        ? {
            chat: true,
            full: t('files.sharedInChatBy', { name: file.uploadedByName }),
            short: file.uploadedByName,
          }
        : { chat: true, full: t('files.sharedInChat'), short: t('files.filterChat') }
    }
    // The viewer's own uploads need no note
    if (byViewer || !file.uploadedByName) return null
    return {
      chat: false,
      full: t('files.uploadedBy', { name: file.uploadedByName }),
      short: file.uploadedByName,
    }
  }

  const renderRow = (file: ILibraryFile, index: number, keyLabel?: string) => {
    // Files shared in chat long ago were saved without a name
    const name = file.name || t('chat.preview.file')
    const [head, tail] = splitFileName(name)
    const { Icon, tone } = CATEGORY_STYLE[getFileCategory(file)]
    const isDeleting = !!deletingId && deletingId === file.id
    const isRecent = !!file.id && highlighted.includes(file.id)
    // A just-uploaded row fades back slowly; hover stays quick everywhere else
    const fadesSlowly = !!file.id && !!recentIds?.includes(file.id)
    const source = sourceOf(file)
    const date = formatFileDate(file.createdAt, i18n.language)
    const size = formatFileSize(file.size)
    const fullMeta = [
      keyLabel,
      formatFileDate(file.createdAt, i18n.language, { withYear: true }),
      source?.full,
      size,
    ]
      .filter(Boolean)
      .join(' · ')
    // Date first and size last: on a narrow screen the size is what gets cut
    const meta: { key: string; node: React.ReactNode; className?: string }[] = []
    if (keyLabel)
      meta.push({ key: 'tag', node: keyLabel, className: 'font-medium text-app-primary-60' })
    if (date) meta.push({ key: 'date', node: date })
    if (source)
      meta.push({
        key: 'source',
        node: (
          <>
            {/* A chat bubble or an upload arrow, then who: the tooltip and screen readers
                get the whole sentence */}
            {source.chat ? (
              <MessageCircle
                aria-hidden="true"
                className={`me-1 inline-block h-3 w-3 align-[-2px] ${inheritColor}`}
              />
            ) : (
              <Upload
                aria-hidden="true"
                className={`me-1 inline-block h-3 w-3 align-[-2px] ${inheritColor}`}
              />
            )}
            <span aria-hidden="true" className={inheritColor}>
              {source.short}
            </span>
            <span className="sr-only">{source.full}</span>
          </>
        ),
      })
    if (size) meta.push({ key: 'size', node: size })

    return (
      <li
        key={file.id || `${name}-${index}`}
        data-recent={isRecent || undefined}
        className={`group relative flex items-center transition-[background-color] hover:bg-app-grey-20 focus-within:bg-app-grey-20 ${
          fadesSlowly ? 'duration-700' : 'duration-150'
        } ${isRecent ? 'bg-app-primary-5' : ''}`}
      >
        <a
          href={file.url || ''}
          target="_blank"
          rel="noreferrer"
          title={name}
          onClick={(e) => openFile(e, file)}
          className={`flex min-w-0 flex-1 items-center gap-3 rounded-lg px-2 py-2 ${focusRing}`}
        >
          <span
            className={`flex h-9 w-9 flex-none items-center justify-center rounded-lg ${tone}`}
            aria-hidden="true"
          >
            <Icon className={`h-[18px] w-[18px] ${inheritColor}`} />
          </span>
          <span className="min-w-0 flex-1">
            {/* Shown in two parts so a long name shortens in the middle and keeps its last
                word; whitespace-pre keeps a space that falls where it is split. Screen
                readers get the name in one piece. The name keeps its own direction
                (dir="auto") but sits on the page's start side. */}
            <span className="sr-only">{name}</span>
            <span aria-hidden="true" className="block text-sm font-medium text-app-black-100">
              <span dir="auto" className="inline-flex max-w-full align-top">
                <span className={`overflow-hidden text-ellipsis whitespace-pre ${inheritColor}`}>
                  {head}
                </span>
                {tail && <span className={`flex-none whitespace-pre ${inheritColor}`}>{tail}</span>}
              </span>
            </span>
            {/* One line. Each part is isolated so sizes, dates and names read right in both
                directions; the whole line is in the tooltip when it is cut. */}
            <span
              title={fullMeta}
              className="mt-0.5 block truncate text-xs leading-4 text-app-grey-65"
            >
              {meta.map((part, i) => (
                <React.Fragment key={part.key}>
                  {i > 0 && <span className={inheritColor}> · </span>}
                  <bdi className={`whitespace-nowrap ${part.className || inheritColor}`}>
                    {part.node}
                  </bdi>
                </React.Fragment>
              ))}
            </span>
          </span>
        </a>
        {onDelete && (
          // With a mouse the button shows on hover, over the end of the row (so names are not
          // cut short for it); on touch screens it is always there, next to the name
          <span
            className={`flex flex-none items-center [@media(hover:hover)_and_(pointer:fine)]:absolute [@media(hover:hover)_and_(pointer:fine)]:inset-y-0 [@media(hover:hover)_and_(pointer:fine)]:end-0 [@media(hover:hover)_and_(pointer:fine)]:bg-app-grey-20 [@media(hover:hover)_and_(pointer:fine)]:pe-1 ${
              isDeleting
                ? ''
                : '[@media(hover:hover)_and_(pointer:fine)]:opacity-0 group-hover:opacity-100 group-focus-within:opacity-100'
            }`}
          >
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 end-full hidden w-8 bg-gradient-to-l from-app-grey-20 to-transparent rtl:bg-gradient-to-r [@media(hover:hover)_and_(pointer:fine)]:block"
            />
            <button
              type="button"
              onClick={() => onDelete(file)}
              disabled={!!deletingId}
              title={t('files.delete')}
              aria-label={t('files.deleteNamed', { name })}
              className={`flex h-10 w-10 items-center justify-center rounded-lg text-app-grey-65 transition hover:bg-app-red-10 hover:text-app-red-70 disabled:cursor-not-allowed ${focusRing}`}
            >
              {isDeleting ? (
                <Loader2 className={`h-4 w-4 animate-spin ${inheritColor}`} />
              ) : (
                <Trash2 className={`h-4 w-4 ${inheritColor}`} />
              )}
            </button>
          </span>
        )}
      </li>
    )
  }

  const showLess = (id: string) => {
    setLimits((prev) => ({ ...prev, [id]: ROWS_SHOWN }))
    // The list got shorter: if its title went off the top of the screen, bring it back
    requestAnimationFrame(() => {
      const head = sectionHeads.current[id]
      if (head && head.getBoundingClientRect().top < 0) head.scrollIntoView({ block: 'start' })
    })
  }

  const renderSection = (
    id: string,
    index: number,
    title: string,
    sectionFiles: ILibraryFile[],
    keyLabel?: (file: ILibraryFile) => string
  ) => {
    // Until someone opens or closes it: with many files, later sections start closed
    // (a search opens them all)
    const isCollapsed =
      collapsed[id] ?? (!activeQuery && groups.total > MANY_FILES && index >= OPEN_SECTIONS)
    // A search shows every match
    const limit = activeQuery ? sectionFiles.length : limits[id] || ROWS_SHOWN
    const visible = sectionFiles.slice(0, limit)
    const remaining = sectionFiles.length - visible.length
    const canShowLess = !activeQuery && limit > ROWS_SHOWN && sectionFiles.length > ROWS_SHOWN
    const linkButton = `flex min-h-[40px] items-center justify-center rounded-lg px-3 text-sm font-medium text-app-primary-60 hover:bg-app-primary-5 ${focusRing}`
    return (
      <section key={id} className="border-t border-app-grey-30 pt-2 first:border-t-0 first:pt-0">
        <button
          ref={(el) => {
            sectionHeads.current[id] = el
          }}
          type="button"
          aria-expanded={!isCollapsed}
          onClick={() => setCollapsed((prev) => ({ ...prev, [id]: !isCollapsed }))}
          className={`flex min-h-[40px] w-full scroll-mt-28 items-center gap-2 rounded-lg px-1 text-start ${focusRing}`}
        >
          <ChevronDown
            aria-hidden="true"
            className={`h-4 w-4 flex-none text-app-grey-65 transition-transform ${
              isCollapsed ? '-rotate-90 rtl:rotate-90' : ''
            }`}
          />
          <span className="text-sm font-semibold text-app-black-100">{title}</span>
          <span className="rounded-full bg-app-grey-20 px-2 py-0.5 text-xs font-medium text-app-grey-65">
            {sectionFiles.length}
          </span>
        </button>
        {!isCollapsed && (
          <>
            <ul className="divide-y divide-app-grey-30">
              {visible.map((file, index) => renderRow(file, index, keyLabel?.(file)))}
            </ul>
            {(remaining > 0 || canShowLess) && (
              <div className="mt-1 flex flex-wrap items-center justify-center gap-1">
                {remaining > 0 && (
                  <button
                    type="button"
                    onClick={() => setLimits((prev) => ({ ...prev, [id]: limit + ROWS_MORE }))}
                    className={linkButton}
                  >
                    {t('files.showMore', { count: Math.min(ROWS_MORE, remaining) })}
                  </button>
                )}
                {canShowLess && (
                  <button type="button" onClick={() => showLess(id)} className={linkButton}>
                    {t('files.showLess')}
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </section>
    )
  }

  const renderBody = () => {
    if (isError) {
      return (
        <div role="alert" className="flex flex-col items-center px-4 py-8 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-app-red-10">
            <AlertCircle aria-hidden="true" className="h-6 w-6 text-app-red-70" />
          </span>
          <p className="mt-3 text-base font-semibold text-app-black-100">
            {t('files.loadFailedTitle')}
          </p>
          <p className="mt-1 max-w-[280px] text-sm text-app-grey-65">{t('files.loadFailedBody')}</p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className={`mt-3 min-h-[40px] rounded-lg px-3 text-sm font-medium text-app-primary-60 hover:bg-app-primary-5 ${focusRing}`}
            >
              {t('files.tryAgain')}
            </button>
          )}
        </div>
      )
    }
    if (isLoading && !shown.length) {
      return (
        <div aria-busy="true" className="space-y-3 py-1">
          <span className="sr-only">{t('files.loading')}</span>
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex animate-pulse items-center gap-3 px-2">
              <span className="h-9 w-9 flex-none rounded-lg bg-app-grey-20" />
              <span className="flex-1 space-y-2">
                <span className="block h-3 w-3/4 rounded bg-app-grey-20" />
                <span className="block h-2.5 w-1/2 rounded bg-app-grey-20" />
              </span>
            </div>
          ))}
        </div>
      )
    }
    if (!shown.length) {
      return (
        <div className="flex flex-col items-center px-4 py-8 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-app-primary-10">
            <FolderOpen aria-hidden="true" className="h-6 w-6 text-app-primary-60" />
          </span>
          <p className="mt-3 text-base font-semibold text-app-black-100">{t('files.emptyTitle')}</p>
          <p className="mt-1 max-w-[280px] text-sm text-app-grey-65">
            {emptyBody || t('files.emptyBody')}
          </p>
        </div>
      )
    }
    if (!groups.total) {
      return (
        <div className="px-4 py-8 text-center">
          <p className="break-words text-sm text-app-grey-65">
            {t('files.noMatch', { query: activeQuery })}
          </p>
          <button
            type="button"
            onClick={() => setQuery('')}
            className={`mt-2 min-h-[40px] rounded-lg px-3 text-sm font-medium text-app-primary-60 hover:bg-app-primary-5 ${focusRing}`}
          >
            {t('files.clearSearch')}
          </button>
        </div>
      )
    }
    return (
      <div ref={listRef} className="space-y-2">
        {groups.key.length > 0 &&
          renderSection('key', 0, t('files.keyDocuments'), groups.key, (file) =>
            file.tag === UserFileTag.BusinessPlan ? t('files.businessPlan') : t('files.logo')
          )}
        {groups.sections.map((section, i) =>
          renderSection(
            section.id,
            i + (groups.key.length > 0 ? 1 : 0),
            t(CATEGORY_STYLE[section.id].label),
            section.files
          )
        )}
      </div>
    )
  }

  const isPlain = variant === 'plain'

  return (
    <div
      {...getRootProps({
        className: `relative [&_svg_*]:[color:inherit] ${
          isPlain ? '' : 'rounded-2xl bg-app-white-100 p-5 shadow sm:p-6'
        }`,
        'data-testid': 'file-library',
      })}
    >
      <input {...getInputProps()} data-testid="file-library-input" />
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <h2
            className={`truncate text-app-black-100 ${
              isPlain ? 'font-primary text-2xl' : 'text-xl font-bold'
            }`}
          >
            {t('files.title')}
          </h2>
          {shown.length > 0 && (
            <span className="inline-flex h-6 min-w-[28px] flex-none items-center justify-center rounded-full bg-app-grey-25 px-2 text-xs font-medium text-app-grey-65">
              {shown.length}
            </span>
          )}
        </div>
        {onUpload && (
          <button
            type="button"
            onClick={open}
            disabled={isUploading}
            className={`inline-flex h-10 flex-none items-center gap-2 rounded-lg border border-app-grey-40 bg-app-white-100 px-3.5 text-sm font-medium text-app-black-100 appShadow-s transition-colors hover:bg-app-grey-20 disabled:cursor-not-allowed disabled:text-app-grey-65 ${focusRing}`}
          >
            {isUploading ? (
              <Loader2 aria-hidden="true" className={`h-4 w-4 animate-spin ${inheritColor}`} />
            ) : (
              <Upload aria-hidden="true" className={`h-4 w-4 ${inheritColor}`} />
            )}
            <span className={inheritColor}>
              {isUploading ? t('files.uploading') : t('files.upload')}
            </span>
          </button>
        )}
      </div>

      {(showSearch || showFilter) && (
        <div className={`${isPlain ? 'mt-6' : 'mt-4'} space-y-3`}>
          {showSearch && (
            <div className="relative">
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-app-grey-65"
              />
              <input
                type="search"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value)
                  // Keep the box on screen as the list above or below it changes
                  e.target.scrollIntoView({ block: 'nearest' })
                }}
                placeholder={t('files.searchPlaceholder')}
                aria-label={t('files.searchPlaceholder')}
                className="h-10 w-full scroll-mt-28 rounded-lg border border-app-grey-40 bg-app-white-100 pe-3 ps-9 text-sm text-app-black-100 placeholder:text-app-grey-60 focus:border-app-primary-60 focus:outline-none focus:appShadow-focus"
              />
            </div>
          )}
          {showFilter && (
            <div
              role="group"
              aria-label={t('files.filterLabel')}
              className="grid grid-cols-3 gap-1 rounded-xl bg-app-grey-20 p-1"
            >
              {FILTERS.map(({ id, label }) => {
                const isActive = activeFilter === id
                return (
                  <button
                    key={id}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => setFilter(id)}
                    // Label and count flow as one centered text, so a narrow button simply
                    // wraps them onto two lines
                    className={`min-h-[40px] min-w-0 rounded-lg px-2 py-1 text-center text-[13px] font-medium leading-tight transition-colors ${focusRing} ${
                      isActive
                        ? 'bg-app-white-100 text-app-black-100 shadow'
                        : 'text-app-grey-65 hover:text-app-black-100'
                    }`}
                  >
                    <span className={inheritColor}>{t(label)}</span>{' '}
                    <span
                      className={`text-xs tabular-nums ${
                        isActive ? 'text-app-primary-60' : inheritColor
                      }`}
                    >
                      {counts[id]}
                    </span>
                  </button>
                )
              })}
            </div>
          )}
          {activeQuery && groups.total > 0 && (
            <p aria-live="polite" className="px-1 text-xs text-app-grey-65">
              {t('files.resultCount', { count: groups.total })}
            </p>
          )}
        </div>
      )}

      <div className={isPlain ? 'mt-6' : 'mt-4'}>{renderBody()}</div>

      {isDragActive && (
        <div
          className={`pointer-events-none absolute inset-0 z-10 border-2 border-dashed border-app-primary-60 bg-app-primary-5 ${
            isPlain ? 'rounded-lg' : 'rounded-2xl'
          }`}
        >
          <div
            className="absolute inset-x-0 flex -translate-y-1/2 flex-col items-center px-6 text-center"
            style={{ top: dropTop ?? 120 }}
          >
            <UploadCloud aria-hidden="true" className="h-9 w-9 text-app-primary-60" />
            <p className="mt-2 text-base font-semibold text-app-primary-60">
              {t('files.dropToUpload')}
            </p>
            <p className="mt-1 text-xs text-app-grey-65">{t('files.dropHint')}</p>
          </div>
        </div>
      )}
    </div>
  )
}
